import { useEffect, useState } from "react";
import { Activity, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

const STAGES = [
    { key: "exploring", label: "Exploring", tone: "border-zinc-700 bg-zinc-900/40 text-zinc-300" },
    { key: "contacted", label: "Contacted", tone: "border-blue-500/40 bg-blue-500/10 text-blue-300" },
    { key: "meeting", label: "Meeting", tone: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
    { key: "closed", label: "Closed", tone: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
];

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${Number(n).toFixed(0)}`;
};

/**
 * Dashboard widget showing pipeline funnel + per-stage avg days + 7d throughput.
 * Reads from GET /api/dashboard/velocity.
 */
export default function VelocityWidget() {
    const [v, setV] = useState(null);

    useEffect(() => {
        api.get("/dashboard/velocity").then(({ data }) => setV(data)).catch(() => setV({ stage_counts: {}, avg_days_in_stage: {}, throughput_7d: 0, total_pipeline_value: 0, total_synergies: 0 }));
    }, []);

    if (!v) return null;

    const maxCount = Math.max(1, ...STAGES.map((s) => v.stage_counts?.[s.key] || 0));

    return (
        <div className="border border-zinc-800 bg-zinc-950 mb-4" data-testid="velocity-widget">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-amber-500" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">Pipeline velocity</div>
                </div>
                <div className="flex items-center gap-4 font-mono text-[10px] text-zinc-500">
                    <div data-testid="velocity-total"><span className="text-zinc-50 font-bold">{v.total_synergies}</span> synergies · <span className="text-amber-400 font-bold">{fmtMoney(v.total_pipeline_value)}</span> projected</div>
                    <Link to="/app/board" className="inline-flex items-center gap-1 text-zinc-500 hover:text-amber-500"><ArrowRight className="w-3 h-3" /> Board</Link>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-zinc-800">
                {STAGES.map((s) => {
                    const count = v.stage_counts?.[s.key] || 0;
                    const days = v.avg_days_in_stage?.[s.key] || 0;
                    const pct = (count / maxCount) * 100;
                    return (
                        <div key={s.key} className="bg-zinc-950 p-4 relative overflow-hidden" data-testid={`velocity-${s.key}`}>
                            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{s.label}</div>
                            <div className="flex items-baseline gap-2">
                                <div className="font-heading font-black text-3xl text-zinc-50">{count}</div>
                                <div className="font-mono text-[10px] text-zinc-500">{days > 0 ? `avg ${days}d` : ""}</div>
                            </div>
                            {/* Funnel bar */}
                            <div className="mt-3 h-1 bg-zinc-900 relative">
                                <div
                                    className={`absolute inset-y-0 left-0 ${s.tone.includes("emerald") ? "bg-emerald-500" : s.tone.includes("amber") ? "bg-amber-500" : s.tone.includes("blue") ? "bg-blue-500" : "bg-zinc-600"}`}
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="px-5 py-3 border-t border-zinc-800 flex items-center gap-2 font-mono text-[10px] text-zinc-500">
                <span className="text-amber-400 font-bold" data-testid="velocity-throughput">{v.throughput_7d}</span>
                <span>moved past Exploring in the last 7 days</span>
            </div>
        </div>
    );
}
